import { Variants, BoxVariants, OldVariants } from '@dayetopia/library';
import { getFlattenedConfiguration } from '@components/shared/utilProductOptions';
import { TAMPON_BOX_TYPES, TamponBoxProductId } from '@constants';
import { TamponType, ProductConfiguration, VariantItem, BoxItem } from '@contracts';

/**
 * Backward-read shim: translate deprecated quarterly-multiplied retail
 * tampon-box SKUs to their per-box equivalents. Apply when reading any
 * stored cart, subscription, or variant state that may have been written
 * before the SKU consolidation. The app no longer writes the deprecated
 * keys; this shim exists only to keep historical state functional.
 */
const DEPRECATED_RETAIL_TAMPON_BOX_ID_MAP: Record<string, TamponBoxProductId> = {
  tampon_box_retail_27cr: TAMPON_BOX_TYPES.CBD_REGULAR_9,
  tampon_box_retail_27cs: TAMPON_BOX_TYPES.CBD_SUPER_9,
  tampon_box_retail_54nr: TAMPON_BOX_TYPES.NUDE_REGULAR_18,
  tampon_box_retail_54ns: TAMPON_BOX_TYPES.NUDE_SUPER_18,
  tampon_box_retail_27nr27cr: TAMPON_BOX_TYPES.MIXED_REGULAR,
  tampon_box_retail_27ns27cs: TAMPON_BOX_TYPES.MIXED_SUPER
};

export function normalizeRetailTamponBoxId(boxId: string): string {
  return DEPRECATED_RETAIL_TAMPON_BOX_ID_MAP[boxId] ?? boxId;
}

export function normalizeRetailTamponBoxVariants(variants: BoxVariants): BoxVariants {
  const normalized: Record<string, number> = {};
  Object.entries(variants).forEach(([key, value]) => {
    if (typeof value !== 'number') return;
    const normalizedKey = normalizeRetailTamponBoxId(key);
    normalized[normalizedKey] = (normalized[normalizedKey] ?? 0) + value;
  });
  return normalized;
}

export function normalizeRetailTamponBoxItems(boxItems: BoxItem[]): BoxItem[] {
  const merged = new Map<string, BoxItem>();
  boxItems.forEach((boxItem) => {
    const normalizedKey = normalizeRetailTamponBoxId(boxItem.key || '');
    const existing = merged.get(normalizedKey);
    if (existing) {
      merged.set(normalizedKey, { ...existing, count: existing.count + boxItem.count });
    } else if (normalizedKey === boxItem.key) {
      merged.set(normalizedKey, boxItem);
    } else {
      merged.set(normalizedKey, { ...boxItem, key: normalizedKey, name: normalizedKey });
    }
  });
  return Array.from(merged.values());
}

/**
 * Remove legacy individual-tampon variant keys (nakedregular, nakedsuper,
 * cbdregular, cbdsuper) from a variants object. Preserves pads, box, and
 * other keys. Used to keep legacy individual-tampon counts out of the new
 * preset-box drawer's payload — Recurly rejects mixing both add-on families.
 */
export function stripLegacyTamponVariants(variants: Variants | BoxVariants | OldVariants | undefined): BoxVariants {
  if (!variants) return {};
  const legacyKeys: string[] = [TamponType.NAKED_REGULAR, TamponType.NAKED_SUPER, TamponType.CBD_REGULAR, TamponType.CBD_SUPER];
  const result: BoxVariants = {};
  Object.entries(variants).forEach(([key, value]) => {
    if (!legacyKeys.includes(key)) {
      (result as Record<string, number>)[key] = value as number;
    }
  });
  return result;
}

/**
 * Check if variants are stack boxes
 * @param variants - Variants or BoxVariants object
 * @returns true if BoxVariants (contains tampon box IDs)
 */
export function isTamponBoxStackBox(variants: Variants | BoxVariants | OldVariants | undefined): boolean {
  if (!variants) return false;

  // Check if any variant key starts with 'tampon_box_retail_'
  return Object.keys(variants).some((key) => key.startsWith('tampon_box_retail_'));
}

/**
 * Check if box items are stack boxes
 * @param variants - Variants or BoxVariants object
 * @returns true if BoxItems (contains tampon box IDs)
 */
export function isTamponStackBox(boxItems: BoxItem[]): boolean {
  if (!boxItems) return false;

  // Check if any box item is a retail tampon box
  return boxItems.some((boxItem) => isRetailTamponBox(boxItem.key || ''));
}

/**
 * Check if variant is a retail tampon box
 * @param variant - Variant string
 * @returns boolean - true if variant is a retail tampon box
 */
export function isRetailTamponBox(variant: string): boolean {
  if (!variant) return false;

  // Check if variant matches the retail tampon box pattern
  // Pattern allows for digits and letters in any combination after 'tampon_box_retail_'
  return variant.match(/tampon_box_retail_[a-z0-9]+$/) !== null;
}

/**
 * Get retail tampon box variants with their counts
 * @param boxVariants - BoxVariants object
 * @returns Record<string, number> - object with retail tampon box variants and their counts
 */
export function getRetailTamponBoxVariants(boxVariants: BoxVariants): Record<string, number> {
  if (!boxVariants) return {};

  const retailVariants: Record<string, number> = {};
  Object.entries(boxVariants).forEach(([key, count]) => {
    if (isRetailTamponBox(key) && count > 0) {
      retailVariants[key] = count;
    }
  });

  return retailVariants;
}

/**
 * Get current total tampon count from variants
 * @param variants - Variants object
 * @returns number - Total tampon count
 */
export const getCurrentTamponCount = (variants: Variants | BoxVariants): number => {
  if (!variants) return 0;

  if (isTamponBoxStackBox(variants)) {
    return getCurrentStackTamponBoxCount(variants as BoxVariants);
  }

  return getCurrentLegacyTamponsCount(variants as Variants);
};

/**
 * Calculate total number of tampon boxes
 * @param variants - Variants object
 * @returns number - Total number of boxes
 */
export const getCurrentStackTamponBoxCount = (variants: BoxVariants): number => {
  if (!variants) return 0;

  const variantsArray = Object.entries(variants);
  const boxes = Object.fromEntries(variantsArray.filter(([key]) => Object.values(TAMPON_BOX_TYPES).some((boxId) => boxId === key)));

  return Object.values(boxes).reduce((total, boxCount) => total + boxCount, 0);
};

/**
 * Get legacy individual tampon count
 * @param variants - Variants object
 * @returns number - Total tampon count
 */
export const getCurrentLegacyTamponsCount = (variants: Variants): number => {
  if (!variants) return 0;
  const tamponIds = [TamponType.NAKED_REGULAR, TamponType.NAKED_SUPER, TamponType.CBD_REGULAR, TamponType.CBD_SUPER];
  const variantsArray = Object.entries(variants);
  const tampons = Object.fromEntries(variantsArray.filter(([key]) => tamponIds.some((tamponId) => tamponId === key)));
  const tamponsCount = Object.values(tampons).length > 0 ? Object.values(tampons).reduce((total, count) => total + count) : 0;
  return Number(tamponsCount) ?? 0;
};

/**
 * Get box display information
 * @param boxType - TamponBoxProductId
 * @returns Object with box details
 */
export function getTamponBoxDisplayInfo(boxType: TamponBoxProductId) {
  const boxInfo = {
    [TAMPON_BOX_TYPES.CBD_REGULAR_9]: {
      name: 'components.cbd-regular-box',
      description: undefined,
      count: 9
    },
    [TAMPON_BOX_TYPES.CBD_SUPER_9]: {
      name: 'components.cbd-super-box',
      description: undefined,
      count: 9
    },
    [TAMPON_BOX_TYPES.NUDE_REGULAR_18]: {
      name: 'components.organic-regular-box',
      description: undefined,
      count: 18
    },
    [TAMPON_BOX_TYPES.NUDE_SUPER_18]: {
      name: 'components.organic-super-box',
      description: undefined,
      count: 18
    },
    [TAMPON_BOX_TYPES.MIXED_REGULAR]: {
      name: 'components.mixed-regular-box',
      description: 'components.mixed-regular-description',
      count: 18
    },
    [TAMPON_BOX_TYPES.MIXED_SUPER]: {
      name: 'components.mixed-super-box',
      description: 'components.mixed-super-description',
      count: 18
    }
  };

  return boxInfo[boxType];
}

export function isCBDBox(productCode: TamponBoxProductId) {
  const isCBD =
    productCode === TAMPON_BOX_TYPES.CBD_REGULAR_9 ||
    productCode === TAMPON_BOX_TYPES.CBD_SUPER_9 ||
    productCode === TAMPON_BOX_TYPES.MIXED_REGULAR ||
    productCode === TAMPON_BOX_TYPES.MIXED_SUPER;

  return isCBD;
}

export function getDropletCount(boxType: TamponBoxProductId) {
  if (boxType === TAMPON_BOX_TYPES.NUDE_SUPER_18 || boxType === TAMPON_BOX_TYPES.CBD_SUPER_9 || boxType === TAMPON_BOX_TYPES.MIXED_SUPER) {
    return 3;
  }
  return 2;
}

/**
 * Process all tampon box variants and flatten them into individual variant counts
 * @param boxVariants - BoxVariants object
 * @param configurations - ProductConfiguration array
 * @returns Record<string, number> - Flattened variants with counts
 */
export function getFlattenedBoxVariants(boxVariants: BoxVariants, configurations: ProductConfiguration[]): Record<string, number> {
  // Start with only non-retail variants to avoid duplication
  const flattenedVariants: Record<string, number> = {};

  // Copy non-retail variants first
  Object.entries(boxVariants).forEach(([key, count]) => {
    if (!isRetailTamponBox(key)) {
      flattenedVariants[key] = count;
    }
  });

  // Check if we have retail tampon box variants to flatten (e.g., tampon_box_retail_9cr9cs)
  const tamponRetailBoxVariants = getRetailTamponBoxVariants(boxVariants);
  if (Object.keys(tamponRetailBoxVariants).length > 0 && configurations.length > 0) {
    Object.entries(tamponRetailBoxVariants).forEach(([retailVariantKey, count]) => {
      const config = configurations.find((c) => c.variant === retailVariantKey);
      if (config) {
        const flattenedItems = getFlattenedConfiguration(config, configurations);

        // Process the flattened items, multiplying by the retail variant count
        flattenedItems.forEach((item: VariantItem) => {
          const existingCount = flattenedVariants[item.variant] || 0;
          flattenedVariants[item.variant] = existingCount + item.count * count;
        });
      }
    });
  }

  return flattenedVariants;
}
